<template>
  <div class="viewer-container">
    <div class="viewer" :class="['typed', `typed--${type}`, 'themed', `themed--${options.theme}`, 'font-sized', 'font-familied', `font-familied--${options.fontFamily}`, options.picked ? 'picked' : '', options.picked ? `picked--${options.picked}` : '', options.split ? 'split' : '', options.split ? `split--${options.split}` : '', options.border ? 'bordered' : '', options.border ? `bordered--${options.border}` : '', options.follow ? 'follow' : '', options.follow ? `follow--${options.follow}` : '']" :style="{ 'font-size': `${options.fontSize}%`}">
      <template v-if="!$apollo.loading">
        <Splitter :body="frameable.body" :preset="type">
          <template v-slot="{ split }">
            <div class="frame">
              <div class="frame-title" :style="{ 'font-size': options.picked === 'title' ? '100%' : `${100 / (Math.max(options.fontSize, 150) / 150)}%` }">
                <template v-if="type === 'document'">
                  <strong>{{ frameable.title }}</strong>
                  <br>
                  <small>{{ frameable.author }} - {{ $date(frameable.date).format('dddd, MMMM D') }}</small>
                </template>

                <template v-else>
                  <span>{{frameable.title}}</span>
                </template>
              </div>
              
              <transition name="fade">
                <div v-if="section % 2 === 0" :key="section" class="frame-subtitle" :style="{ 'font-size': options.picked === 'subtitle' ? '100%' : `${100 / (Math.max(options.fontSize, 150) / 150)}%` }">
                  <span>{{split[section].title}}</span>
                </div>
                <div v-else :key="section" class="frame-subtitle" :style="{ 'font-size': options.picked === 'subtitle' ? '100%' : `${100 / (Math.max(options.fontSize, 150) / 150)}%` }">
                  <span>{{split[section].title}}</span>
                </div>
              </transition>

              <transition name="fade">
                <Foo v-if="section % 2 === 0" :key="section" class="frame-body" :content="split[section].body" :index="position" :splittable="type !== 'song'" :split="options.split"></Foo>

                <Foo v-else :key="section" class="frame-body" :content="split[section].body" :index="position" :splittable="type !== 'song'" :split="options.split"></Foo>
              </transition>

              <div v-if="type == 'song'" class="frame-meta" :style="{ 'font-size': `${100 / (Math.max(options.fontSize, 150) / 150)}%` }">
                 <span v-if="frameable.key">Key of {{ frameable.key }}</span>
              </div>
            </div>
          </template>
        </Splitter>
      </template>
    </div>
  </div>
</template>

<script>
  import gql from 'graphql-tag'

  import Splitter from '../components/Splitter';
  import Foo from '../components/Foo';

  const GET_DOCUMENT = gql`
    query GetDocument($id: uuid!) {
      frameable: documents_by_pk(id: $id) {
        id
        title
        date
        author
        body
      }
    }
  `;

  const GET_SNIPPET = gql`
    query GetSnippet($id: uuid!) {
      frameable: snippets_by_pk(id: $id) {
        id
        title
        body
      }
    }
  `;

  const GET_SONG = gql`
    query GetSong($id: uuid!) {
      frameable: songs_by_pk(id: $id) {
        id
        title
        body
        key
        author
        key
      }
    }
  `;

  export default {
    props: {
      type: String,
      id: String,
      timestamp: String,

      section: {
        type: Number,
        default: 0
      },
      position: {
        type: Number,
        default: 0
      },

      fontSize: {
        type: Number,
        default: 100
      },

      options: {
        type: Object
      }
    },

    components: {
      Splitter,
      Foo
    },

    data() {
      return {
        frameable: {}
      }
    },

    watch: {
      timestamp() {
        this.$apollo.queries.frameable.refetch();
      }
    },

    apollo: {
      frameable: {
        query() {
          return {
            document: GET_DOCUMENT,
            snippet: GET_SNIPPET,
            song: GET_SONG
          }[this.type]
        },

        variables() {
          return {
            id: this.id
          }
        }
      }
    }
  }
</script>

<style scoped>
  .viewer-container {
    font-size: 2em;
  }

  .viewer {
    position: relative;
    height: 100vh;

    min-width: 12em;
  }

  .viewer.picked {

  }

  .viewer.picked .frame-title, .viewer.picked .frame-subtitle, .viewer.picked .frame-body, .viewer.picked .frame-meta {
    display: none !important;
  }

  .viewer.picked--title .frame-title, .viewer.picked--subtitle .frame-subtitle {
    text-align: left !important;
  }

  .viewer.picked--title .frame-title {
    display: block !important;
    top: 0 !important;
    left: 0 !important;
  }

  .viewer.picked--subtitle .frame-subtitle {
    display: block !important;
    top: 0 !important;
    bottom: auto !important;
  }

  .viewer.typed {

  }

  .viewer.typed--document {

  }

  .viewer.typed--document .frame-title, .viewer.typed--document .frame-subtitle {
    display: none;
  }

  .viewer.typed--song .frame-subtitle {
    display: none;
  }

  .viewer.themed {

  }

  .viewer.typed--song.themed--default {
    color: #fff;
    background: rgb(30, 78, 128);
  }

  .viewer.typed--document.themed--default {
    color: #333;
    background: rgb(245, 225, 170);
  }

  .viewer.themed--light {
    color: #000;
    background: #fff;
  }
  
  .viewer.themed--dark {
    color: #ddd;
    background: #000;
  }

  .viewer.themed--dark-inverted {
    color: #ddd;
    background: #000;
  }

  .viewer.themed--dark-inverted /deep/ .foo[style*="background-color:"] {
    color: #000;
  }

  .viewer.themed--dark-inverted /deep/ .foo > span {
    background: inherit;
  }

  .viewer.themed--blue {
    color: #fff;
    background: rgb(30, 78, 128);
  }

  .viewer.themed--chroma {
    color: #fff;
    background: rgb(35, 98, 175);
  }

  .viewer.themed--gold {
    color: #333;
    background: rgb(245, 225, 170);
  }

  .viewer.font-sized {
    
  }

  .viewer.font-familied {
  
  }

  .viewer.font-familied--default {
    
  }

  .viewer.font-familied--serif {
    font-family: Georgia, 'Times New Roman', Times, serif;
  }

  .viewer.font-familied--sans-serif {
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  }

  .frame {
    height: 100%;
    width: 100%;

    position: relative;
  }

  .frame-title, .frame-subtitle {
    position: absolute;
    top: 0;
    width: 100%;
    text-align: center;
    text-decoration: underline;
    opacity: 0.85;

    padding: 0.5rem;

    z-index: 1;
  }

  .frame-subtitle > span {
    font-size: 0.75em;    
  }

  .viewer.typed--song .frame-body /deep/ .content {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    padding-left: 1em;
  }

  .viewer.typed--song .frame-body /deep/ .content p {
    margin-bottom: 0;
    text-indent: -1em;
  }

  /deep/ .frame-body .ql-font-serif {
    font-family: Georgia, 'Times New Roman', Times, serif;
  }

  /deep/ .frame-body code {
    color: inherit !important;
    opacity: 0.35;
  }

  .follow--disabled /deep/ .frame-body .foo {
    opacity: 1.0 !important;
  }

  .frame-meta {
    position: absolute;
    bottom: 0;
    right: 0;
    padding: 0.5rem 1rem;
  }

  .frame-meta > span {
    font-size: 0.65em;
  }

  @media (max-width: 500px) {
    .frame {
      padding: 1rem;
    }
  }

  .viewer.typed--song.themed--default .frame-title, .viewer.typed--song.themed--blue .frame-title,
  .viewer.typed--song.themed--default .frame-subtitle, .viewer.typed--song.themed--blue .frame-subtitle {
    background: rgb(30, 78, 128);
  }

  .viewer.typed--song .frame-content {
    padding: 0;
  }

  .viewer.typed--song .frame-subtitle {
    display: block;
    top: auto;
    bottom: 0;

    opacity: 0.75em;
    font-size: 0.75em;
    text-decoration: none;
  }

  .viewer.bordered--fade-top-black:after {
    content: '';

    position: absolute;
    top: 0;
    width: 100%;
    height: 1em;

    background: linear-gradient(rgba(0, 0, 0, 0.85), rgba(0, 0, 0, 0));
    z-index: 100;
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.25s ease-in-out;
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0
  }
</style>